/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 1:12:41 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, December 29th 2021, 11:38:00 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { schemaBuilder } from "lib/validation";
import {
  ProcessTemplateArtifact,
  ProcessTemplateArtifactValidationSchema,
  ProcessTemplateArtifactSchemaType,
  USER_TYPES,
} from "lib/schemas";
import { WritableModel } from "lib/schemas/abstract";
import { MediaAssetMetaDataValidationSchema } from "lib/schemas/media-library/";
import { Many2ManyIntPKBaseSchema /*fileBaseSchema*/ } from "lib/validation/base-validation-schemas";
import { UUID4 } from "lib/types";

export enum STARTEVENTS {
  form_sub = "Form Submission",
  email = "Email",
  sms = "SMS",
}

export enum PROCESSSTATUS {
  DEPLOYED = "Live",
  PRE_DEPLOYMENT_TESTING = "Testing",
  DEVELOPMENT = "Development",
}

export interface ProcessDefinitionExtraConfigurations {
  call_to_action_text?: string;
  is_restricted_to_workers?: boolean;
  worker_can_submit_for_client?: boolean;
  exclude_from_services_menu?: boolean;
  service_side_menu_icon?: string;
  upstream_selection_label?: string;
  upstream_selection_description?: string;
}

interface FlowChartViewportState {
  x_position_factor: number;
  y_position_factor: number;
  zoom_level?: number;
}

export interface ProcessDefinitionExtraData {
  flowchart_viewport_state: FlowChartViewportState;
}

export interface ProcessDefinition extends ProcessTemplateArtifact, WritableModel<false> {
  title: string;
  service_code: string;
  description?: string;
  short_description?: string;
  quick_guide?: string;
  tags?: string[];
  start_event: keyof typeof STARTEVENTS;
  submission_forms: number[];
  multiple_submission_forms_cta?: string;
  status: keyof typeof PROCESSSTATUS;
  detail_view_layout?: number;
  list_view_layout?: number;
  site: number;
  readonly site_name?: string;
  readonly site_url?: string;
  other_sites?: number[];
  service_unit?: string;
  category?: number;
  readonly category_name?: string;
  sequence: number;
  related_processes?: number[];
  allow_staff_edit?: boolean;
  allow_edit_for_staff_assigned?: number;
  allow_edit_for_stage?: number;
  document_list_view_layout?: number;
  service_registration_forms?: number[];
  department?: UUID4;
  readonly department_name?: string;
  groups?: number[];
  service_logo?: string;
  service_email_notifications_layout?: number;
  source_pre_built_template?: UUID4;
  readonly source_pre_built_template_name?: string;
  readonly service_dashboard_url?: string;
  readonly is_initiated_by_client?: boolean;
  readonly is_initiated_by_worker?: boolean;
  extra_configurations?: ProcessDefinitionExtraConfigurations;
  extra_data?: ProcessDefinitionExtraData;
}

export interface ProcessDefinitionSubmissionFormLinkage extends ProcessTemplateArtifact, WritableModel<false> {
  id: number;
  allowed_initiator_types?: (keyof typeof USER_TYPES)[];
  processdefinition: ProcessDefinition["id"];
  form: ProcessDefinition["submission_forms"][0];
  readonly form_detail?: { id: number; title: string; slug: string };
}

const ProcessDefinitionExtraConfigurationsValidationSchema: schemaBuilder.SchemaOf<ProcessDefinitionExtraConfigurations> =
  schemaBuilder.object().shape({
    call_to_action_text: schemaBuilder.string().optional(),
    is_restricted_to_workers: schemaBuilder.boolean().optional(),
    worker_can_submit_for_client: schemaBuilder.boolean().optional(),
    exclude_from_services_menu: schemaBuilder.boolean().optional(),
    service_side_menu_icon: schemaBuilder.string().optional(),
    upstream_selection_label: schemaBuilder.string().optional(),
    upstream_selection_description: schemaBuilder.string().optional(),
  });

const FlowChartViewportStateValidationSchema: schemaBuilder.SchemaOf<FlowChartViewportState> = schemaBuilder
  .object()
  .shape({
    x_position_factor: schemaBuilder.number().optional(),
    y_position_factor: schemaBuilder.number().optional(),
    zoom_level: schemaBuilder.number().optional(),
  });

const ProcessDefinitionExtraDataValidationSchema: schemaBuilder.SchemaOf<ProcessDefinitionExtraData> = schemaBuilder
  .object()
  .shape({
    flowchart_viewport_state: FlowChartViewportStateValidationSchema.optional(),
  });

export type ProcessDefinitionPatchBody = Partial<Omit<ProcessDefinition, "id">>;

type WriteOnlyProcessDefinition = Omit<
  ProcessDefinition,
  | "id"
  | "site_name"
  | "site_url"
  | "category_name"
  | "department_name"
  | "source_pre_built_template_name"
  | "service_dashboard_url"
  | "is_initiated_by_client"
  | "is_initiated_by_worker"
  | "artifact_guid"
  | "artifact_tag"
>;

export type ProcessDefinitionPatchOptions = Partial<Omit<WriteOnlyProcessDefinition, "artifact_org">>;

export const ProcessDefinitionValidationSchema: ProcessTemplateArtifactSchemaType<WriteOnlyProcessDefinition> =
  ProcessTemplateArtifactValidationSchema.shape({
    id: schemaBuilder.number().positive().nullable(),
    title: schemaBuilder.string().required("You must provide the title for a configured service."),
    service_code: schemaBuilder.string().required("You must provide a service code for a configured service."),
    description: schemaBuilder.string().nullable().optional(),
    short_description: schemaBuilder.string().nullable().optional(),
    quick_guide: schemaBuilder.string().nullable().optional(),
    tags: schemaBuilder.array(schemaBuilder.string()).nullable().optional(),
    start_event: schemaBuilder
      .mixed()
      .oneOf(Object.keys(STARTEVENTS))
      .required("Start Event is required")
      .default(STARTEVENTS.form_sub),
    submission_forms: Many2ManyIntPKBaseSchema().required("Submission form(s) required for a service"),
    multiple_submission_forms_cta: schemaBuilder.string().nullable().optional(),
    status: schemaBuilder
      .mixed()
      .oneOf(Object.keys(PROCESSSTATUS))
      .required("Process Workflow Template Status is required")
      .default("DEVELOPMENT"),
    detail_view_layout: schemaBuilder.number().nullable().optional(),
    list_view_layout: schemaBuilder.number().nullable().optional(),
    site: schemaBuilder
      .number()
      .required("You must set a portal site where the service will be deployed")
      .typeError("A valid portal site is required."),
    other_sites: Many2ManyIntPKBaseSchema().nullable().optional(),
    service_unit: schemaBuilder.string().nullable().optional(),
    category: schemaBuilder.number().nullable().optional(),
    category_name: schemaBuilder.string().nullable().optional(),
    sequence: schemaBuilder.number().required("Sequence is required."),
    related_processes: Many2ManyIntPKBaseSchema().nullable().optional(),
    allow_staff_edit: schemaBuilder.boolean().nullable().optional(),
    allow_edit_for_staff_assigned: schemaBuilder.number().nullable().optional(),
    allow_edit_for_stage: schemaBuilder.number().nullable().optional(),
    document_list_view_layout: schemaBuilder.number().nullable().optional(),
    service_registration_forms: Many2ManyIntPKBaseSchema().nullable().optional(),
    department: schemaBuilder.string().uuid().optional().nullable().typeError("A valid department is required."),
    groups: Many2ManyIntPKBaseSchema().nullable().optional(),
    // service_logo: fileBaseSchema({ sizeLimit: 2097152 }).nullable(),
    service_logo: MediaAssetMetaDataValidationSchema.optional().nullable().default(null),
    service_email_notifications_layout: schemaBuilder.number().nullable().optional(),
    source_pre_built_template: schemaBuilder.string().uuid().nullable().optional(),
    extra_configurations: ProcessDefinitionExtraConfigurationsValidationSchema.optional(),
    extra_data: ProcessDefinitionExtraDataValidationSchema.optional(),
  });

type WritableProcessDefinitionSubmissionFormLinkage = Omit<ProcessDefinitionSubmissionFormLinkage, "form_detail">;

export const ProcessDefinitionSubmissionFormLinkageValidationSchema: ProcessTemplateArtifactSchemaType<WritableProcessDefinitionSubmissionFormLinkage> =
  ProcessTemplateArtifactValidationSchema.shape({
    id: schemaBuilder.number().positive().nullable(),
    form: schemaBuilder
      .number()
      .required("You must set a form for the linkage")
      .typeError("A valid form id is required."),
    processdefinition: schemaBuilder
      .number()
      .required("You must set a process for the linkage")
      .typeError("A valid process definition id is required."),
    allowed_initiator_types: schemaBuilder
      .array(schemaBuilder.mixed().oneOf(Object.keys(USER_TYPES)))
      .required("Allowed initiator types required for a service"),
  });

export type ProcessInstanceDataTableResult = {
  draw: number;
  recordsTotal: number;
  recordsFiltered: number;
  data: (null | number | string)[][];
};
